// Position persistence service for maintaining position data during API failures
import type { Position } from '../types/mexc';

const POSITION_STORAGE_KEY = 'mexc_positions_cache';
const POSITION_METADATA_KEY = 'mexc_positions_metadata';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
// const CONNECTION_STATUS_KEY = 'mexc_connection_status'; // Unused, commented out

export interface PositionCacheMetadata {
  lastUpdate: number;
  lastSuccessfulFetch: number;
  consecutiveFailures: number;
  isConnected: boolean;
  lastError?: {
    code: number;
    message: string;
    timestamp: number;
  };
}

export interface ConnectionStatus {
  isConnected: boolean;
  lastSuccessfulFetch: number;
  consecutiveFailures: number;
  lastError?: {
    code: number;
    message: string;
    timestamp: number;
  };
}

export class PositionPersistenceService {
  /**
   * Save positions to localStorage with metadata
   */
  static savePositions(positions: Position[]): void {
    try {
      const now = Date.now();
      const metadata: PositionCacheMetadata = {
        lastUpdate: now,
        lastSuccessfulFetch: now,
        consecutiveFailures: 0,
        isConnected: true,
      };

      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(positions));
      localStorage.setItem(POSITION_METADATA_KEY, JSON.stringify(metadata));
      // Silently save - verbose logging not needed for routine cache updates
    } catch (error) {
      console.error('Failed to save positions to cache:', error);
    }
  }

  /**
   * Load positions from localStorage
   */
  static loadPositions(): Position[] {
    try {
      const cachedData = localStorage.getItem(POSITION_STORAGE_KEY);
      if (!cachedData) {
        return [];
      }

      const positions = JSON.parse(cachedData) as Position[];
      const metadata = this.getMetadata();
      
      // Check if cache is expired
      if (this.isCacheExpired(metadata)) {
        console.warn('⚠️ Position cache expired, clearing...');
        this.clearCache();
        return [];
      }

      // Silently load - verbose logging not needed for routine cache access
      return positions;
    } catch (error) {
      console.error('Failed to load positions from cache:', error);
      return [];
    }
  }

  /**
   * Record API failure and update connection status
   */
  static recordFailure(error: { code?: number; message?: string }): void {
    try {
      const metadata = this.getMetadata();
      const now = Date.now();
      
      const updatedMetadata: PositionCacheMetadata = {
        ...metadata,
        lastUpdate: now,
        consecutiveFailures: metadata.consecutiveFailures + 1,
        isConnected: false,
        lastError: {
          code: error.code || 0,
          message: error.message || 'Unknown error',
          timestamp: now,
        },
      };

      localStorage.setItem(POSITION_METADATA_KEY, JSON.stringify(updatedMetadata));
      // Only log failures when they start accumulating (3+ consecutive)
      if (updatedMetadata.consecutiveFailures >= 3) {
        console.warn('⚠️ Multiple API failures:', updatedMetadata.consecutiveFailures, updatedMetadata.lastError);
      }
    } catch (error) {
      console.error('Failed to record API failure:', error);
    }
  }

  /**
   * Record successful API call
   */
  static recordSuccess(): void {
    try {
      const now = Date.now();
      const metadata: PositionCacheMetadata = {
        lastUpdate: now,
        lastSuccessfulFetch: now,
        consecutiveFailures: 0,
        isConnected: true,
      };

      localStorage.setItem(POSITION_METADATA_KEY, JSON.stringify(metadata));
      // Silently record success - routine operation
    } catch (error) {
      console.error('Failed to record API success:', error);
    }
  }

  /**
   * Get cache metadata
   */
  static getMetadata(): PositionCacheMetadata {
    try {
      const metadataStr = localStorage.getItem(POSITION_METADATA_KEY);
      if (!metadataStr) {
        return {
          lastUpdate: 0,
          lastSuccessfulFetch: 0,
          consecutiveFailures: 0,
          isConnected: false,
        };
      }

      return JSON.parse(metadataStr) as PositionCacheMetadata;
    } catch (error) {
      console.error('Failed to load cache metadata:', error);
      return {
        lastUpdate: 0,
        lastSuccessfulFetch: 0,
        consecutiveFailures: 0,
        isConnected: false,
      };
    }
  }

  /**
   * Get connection status for UI indicators
   */
  static getConnectionStatus(): ConnectionStatus {
    const metadata = this.getMetadata();
    return {
      isConnected: metadata.isConnected,
      lastSuccessfulFetch: metadata.lastSuccessfulFetch,
      consecutiveFailures: metadata.consecutiveFailures,
      lastError: metadata.lastError,
    };
  }

  /**
   * Check if cache is expired
   */
  static isCacheExpired(metadata: PositionCacheMetadata): boolean {
    if (metadata.lastUpdate === 0) return true;
    return Date.now() - metadata.lastUpdate > CACHE_EXPIRY_MS;
  }

  /**
   * Clear all cached data
   */
  static clearCache(): void {
    try {
      localStorage.removeItem(POSITION_STORAGE_KEY);
      localStorage.removeItem(POSITION_METADATA_KEY);
      console.log('🗑️ Position cache cleared');
    } catch (error) {
      console.error('Failed to clear position cache:', error);
    }
  }

  /**
   * Get positions with fallback logic
   * Returns cached positions if API is failing
   * IMPORTANT: Empty array from API means no positions - must clear cache
   */
  static getPositionsWithFallback(
    apiPositions: Position[] | undefined,
    _isLoading: boolean, // Parameter kept for API compatibility but not used
    error: any
  ): { positions: Position[]; isUsingCache: boolean; connectionStatus: ConnectionStatus } {
    const connectionStatus = this.getConnectionStatus();

    // If API call succeeded (no error and data is defined - even if empty array)
    if (!error && apiPositions !== undefined) {
      // API returned valid data - could be empty array (no positions) or positions
      if (apiPositions.length > 0) {
        this.savePositions(apiPositions);
      } else {
        // API returned empty array = user has no positions on MEXC
        // Clear the cache to prevent showing stale positions
        this.clearCache();
        console.log('📭 API returned 0 positions - cache cleared to reflect actual MEXC state');
      }
      this.recordSuccess();
      return {
        positions: apiPositions,
        isUsingCache: false,
        connectionStatus: { ...connectionStatus, isConnected: true },
      };
    }

    // If API failed, record the failure
    if (error) {
      this.recordFailure({
        code: error.response?.data?.code || error.code,
        message: error.response?.data?.message || error.message,
      });
    }

    // Load cached positions as fallback (only when API fails)
    const cachedPositions = this.loadPositions();

    return {
      positions: cachedPositions,
      isUsingCache: cachedPositions.length > 0,
      connectionStatus: this.getConnectionStatus(),
    };
  }

  /**
   * Should we show "No Positions" vs "Connection Issues"?
   */
  static shouldShowNoPositions(
    positions: Position[],
    connectionStatus: ConnectionStatus
  ): { showNoPositions: boolean; showConnectionIssues: boolean } {
    // If we have positions (cached or fresh), don't show "no positions"
    if (positions.length > 0) {
      return { showNoPositions: false, showConnectionIssues: !connectionStatus.isConnected };
    }

    // No positions and connected = truly no positions
    if (connectionStatus.isConnected) {
      return { showNoPositions: true, showConnectionIssues: false };
    }

    // No positions and disconnected = connection issues
    return { showNoPositions: false, showConnectionIssues: true };
  }
}
